import React, { useEffect, useState } from 'react';
import { Typography, DialogActions, DialogContent, DialogTitle, Grid, IconButton, MenuItem, Select, Box, Stack, CircularProgress, TextField, FormControl, FormControlLabel, Checkbox } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
// icons
import { grey } from "@mui/material/colors";
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from "common/customIcons/delete";
// utils and helpers
import { handleUploadFile } from "services/fileUpload";
import { openSnackbar } from '../../../redux/slices/common';
import { closeCustomModalDialog, openCustomModalDialog, setModalLoader } from '../../../redux/slices/modalSlice';
import { addDiscrepancy, deleteDiscrepancy, getDiscrepancy, getDistributionConfig, getProductDetails, updateDiscrepancy } from "../../../redux/slices/hyperwolf/inventoryDistribution";
import { showToastMsg } from "components/distributionSetting/distributionManagement/addRegionKit";
// custom components
import { circularProgressIcon } from "layouts/hyperwolf/regions/components/addSubRegion";
import MDButton from 'components/MDButton';
import { numberOnly } from "utilities/common";
import { validateDriverDiscrepancy } from "validations/hyperwolf/distributionmanagement";

export default function AddDiscrepancyNote({ closeModal }) {
  const dispatch = useDispatch();

  const { user } = useSelector(state => state.auth || {});
  const { modalData, isOpen, isLoading } = useSelector(state => state.modal);
  const { distributionId, driverId, productId, discrepancyId, handleDriverProducts = () => { }, isNewProduct } = modalData || {}

  const [productDetails, setProductDetails] = useState({})
  const [details, setDetails] = useState({})
  const [discrepancyTypes, setDiscrepancyTypes] = useState([])
  const [isUploadingFile, setIsUploadingFile] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [errors, setErrors] = useState({})

  const handleGetDiscrepancy = async () => {
    try {
      const response = await dispatch(getDiscrepancy(discrepancyId)).unwrap();
      const { data } = response || {};
      if (data) {
        setDetails(prev => ({ ...prev, ...data }));
      }
    }
    catch (error) {
      showToastMsg(error, "error");
    }
  };

  const handleProductDetails = async () => {
    try {
      const response = await dispatch(getProductDetails({ distributionId, driverId, productId })).unwrap();
      const { data } = response || {};
      if (data) {
        setProductDetails(data || {});
      }
    }
    catch (error) {
      showToastMsg(error, "error");
    }
  };

  const handleGetConfig = async () => {
    try {
      const response = await dispatch(getDistributionConfig()).unwrap()
      const data = response?.data;
      if (data) {
        setDiscrepancyTypes(data?.discrepancyTypes || []);
      }
    } catch (error) {
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setDetails(prev => ({ ...prev, [name]: name === "discrepancyQty" ? numberOnly(value) : value }))
  }

  const handleChangeBatch = (e) => {
    const { name, checked, value, qty } = e.target;

    setDetails(prev => {
      if (name === "batchNo") {
        const discrepancyBatches = prev?.discrepancyBatches?.filter((item) => item.batchNo !== value.batchNo) || [];
        return checked ? { ...prev, discrepancyBatches: [...discrepancyBatches, { ...value, qty: "" }] } : { ...prev, discrepancyBatches: discrepancyBatches }
      } else {
        const index = prev?.discrepancyBatches?.findIndex((item) => item.batchNo === value.batchNo);
        if (index === -1) return { ...prev };

        const updatedBatches = prev?.discrepancyBatches;
        updatedBatches[index].qty = qty;

        return { ...prev, discrepancyBatches: updatedBatches }
      }
    })
  }

  const handleFileUpload = async (e) => {
    if (isUploadingFile) return;

    try {
      setIsUploadingFile(true);

      const { files } = e.target;
      const file = files[0];

      if (!file) return; // Validate file is not empty

      // Validate file is an image
      const isImage = !file.type || (file && file.type && file.type.toLowerCase().startsWith("image/"));
      if (!isImage) {
        showToastMsg("Upload a valid image file", "error");
        return;
      }

      const formData = new FormData();
      formData.append("filePath", file);

      const fileResponse = await handleUploadFile(formData);
      if (fileResponse?.fileUrl) {
        setDetails(prev => ({ ...prev, photos: [...(prev.photos || []), fileResponse?.fileUrl] }));
      }
    }
    catch (error) {
      showToastMsg(error, "error");
    } finally {
      setIsUploadingFile(false);
    }
  }

  const handleDeletePhoto = (image) => {
    setDetails(prev => ({ ...prev, photos: prev.photos.filter((img) => img !== image) }))
  }

  const handleSubmit = async () => {
    try {
      const { myErrors, isValid } = validateDriverDiscrepancy(details)

      if (!isValid) {
        setErrors(myErrors);
        return;
      } else {
        setErrors({});
      }

      dispatch(setModalLoader(true));
      const request = {
        distributionId, driverId, productId,
        regionId: productDetails?.regionId || "",
        subRegionId: productDetails?.subRegionId || "",
        boxId: productDetails?.box?.id || "",
        createdBy: user?.name || "",
        ...details,
      }

      let response = {}
      if (discrepancyId) {
        response = await dispatch(updateDiscrepancy({ ...request, discrepancyId })).unwrap();
      } else {
        response = await dispatch(addDiscrepancy({ ...request })).unwrap();
      }
      showToastMsg(response?.message, 'success');
      dispatch(closeCustomModalDialog());

      if (response?.success) {
        handleDriverProducts()
      }
    } catch (err) {
      showToastMsg(err, 'error');
    } finally {
      dispatch(setModalLoader(false));
    };

  }

  const handleDelete = async () => {
    if (isDeleting || !discrepancyId) return;
    try {
      setIsDeleting(true);

      const response = await dispatch(deleteDiscrepancy(discrepancyId)).unwrap();
      if (response?.success) {
        showToastMsg(response?.message, 'success');
        handleDriverProducts()
      }
    } catch (err) {
      showToastMsg(err, 'error');
    } finally {
      setIsDeleting(false);
    };
  }

  useEffect(() => {
    if (isOpen) {
      handleProductDetails()
      handleGetConfig()
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && discrepancyId) {
      handleGetDiscrepancy()
    }
  }, [isOpen, discrepancyId]);

  return (
    <>
      <DialogTitle className='pt-4 px-4 pb-1'>
        <Typography variant="h4" fontWeight="bold">
          {discrepancyId ? "Discrepancy Note" : "Add Discrepancy Note"}
        </Typography>
        <IconButton onClick={closeModal} edge='end' size='small' style={{ position: "absolute", right: "24px", top: "24px" }}>
          <CloseIcon fontSize='medium' />
        </IconButton>
      </DialogTitle>

      <DialogContent className='px-4 py-0'>
        <Typography variant="body2" sx={{ mb: 3, color: "#656575" }}>
          Upload photo or add discrepancy note
        </Typography>

        <Grid container spacing={3}>
          {/* Product details */}
          <Grid item xs={12} md={12}>
            <ul className="w-100 d-flex f-wrap pb-2" style={{ gap: "20px" }}>
              <li className="d-flex flex-column" style={{ width: "calc(33% - 20px)" }}>
                <Typography variant='body2' fontWeight="500" color="#656575">Product Name</Typography>
                <Typography variant='body2' fontWeight="600" color="#2E2E3A">{productDetails?.productName}</Typography>
              </li>
              <li className="d-flex flex-column" style={{ width: "calc(33% - 20px)" }}>
                <Typography variant='body2' fontWeight="500" color="#656575">SKU</Typography>
                <Typography variant='body2' fontWeight="600" color="#2E2E3A">{productDetails?.sku}</Typography>
              </li>
              <li className="d-flex flex-column" style={{ width: "calc(33% - 20px)" }}>
                <Typography variant='body2' fontWeight="500" color="#656575">Brand</Typography>
                <Typography variant='body2' fontWeight="600" color="#2E2E3A">{productDetails?.brand?.name || "-"}</Typography>
              </li>
              <li className="d-flex flex-column" style={{ width: "calc(33% - 20px)" }}>
                <Typography variant='body2' fontWeight="500" color="#656575">Expected Quantity</Typography>
                <Typography variant='body2' fontWeight="600" color="#2E2E3A">{productDetails?.expectedQty || "-"}</Typography>
              </li>
              <li className="d-flex flex-column" style={{ width: "calc(33% - 20px)" }}>
                <Typography variant='body2' fontWeight="500" color="#656575">Scanned Quantity</Typography>
                <Typography variant='body2' fontWeight="600" color="#2E2E3A">{productDetails?.scannedQty || "-"}</Typography>
              </li>
            </ul>
          </Grid>

          <Grid item xs={12}>
            <hr className='my-1' style={{ borderTop: "1px solid #D0D0DA", borderBottom: "none", borderLeft: "none", borderRight: "none" }} />
          </Grid>

          {/* Discrepancy batch */}
          {
            productDetails?.batches?.map(batch => {
              const selectedBatch = details?.discrepancyBatches?.find(b => b.batchNo === batch.batchNo) || {};
              const selectedBatchError = Array.isArray(errors?.discrepancyBatches) && errors?.discrepancyBatches?.find(b => b.batchNo === batch.batchNo) || {};

              return (
                <Grid item xs={12} key={batch.batchNo}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <FormControl style={{ minWidth: "170px" }}>
                      <FormControlLabel
                        control={<Checkbox />}
                        name="batchNo"
                        disabled={isNewProduct}
                        label={batch?.sku}
                        value={batch}
                        checked={selectedBatch?.batchNo ? true : false}
                        onChange={e => handleChangeBatch({ target: { name: "batchNo", value: batch, checked: e.target.checked } })}
                      />
                    </FormControl>

                    <Box>
                      <TextField
                        fullWidth
                        variant="outlined"
                        className="form-input bg-transparent"
                        placeholder="Add Qty"
                        disabled={!selectedBatch?.batchNo || isNewProduct}
                        name="qty"
                        value={selectedBatch?.qty || ""}
                        onChange={e => {
                          const { value } = e.target;

                          const maxAvailableQty = Math.abs(batch.qty - (batch.scannedQty || 0));
                          const quantity = Math.abs(numberOnly(value)) > maxAvailableQty ? maxAvailableQty : numberOnly(value);
                          if (parseInt(quantity) === 0) return;
                          handleChangeBatch({ target: { name: "qty", value: batch, qty: quantity } });
                        }}
                      />
                      {selectedBatchError?.error && <p className="error">{selectedBatchError?.error}</p>}
                    </Box>
                  </Box>
                </Grid>
              )
            })
          }
          {errors?.discrepancyBatches && typeof errors?.discrepancyBatches === "string" &&
            <p className="error" style={{ width: "100%", padding: "5px 24px 0" }}>{errors?.discrepancyBatches}</p>
          }

          {/* Discrepancy Type */}
          <Grid item xs={12}>
            <Typography variant='body2' fontWeight="medium" color="#656575" mb={1}>Select Discrepancy</Typography>
            <Select
              size='small'
              fullWidth
              className='form-select'
              name="discrepancyType"
              disabled={isNewProduct}
              value={details?.discrepancyType || ""}
              onChange={handleChange}
            >
              {
                discrepancyTypes?.map(discrepancy => (
                  <MenuItem key={discrepancy} value={discrepancy}>{discrepancy}</MenuItem>
                ))
              }
            </Select>
            {errors?.discrepancyType && <p className="error">{errors?.discrepancyType}</p>}
          </Grid>

          {/* Discrepancy Note */}
          <Grid item xs={12}>
            <textarea
              placeholder='Add Note...'
              style={{
                width: "100%", minHeight: "100px", borderRadius: "20px", boxShadow: 'none', border: "1px solid #9A9AAF", overflow: "hidden", outline: "none", padding: "12px 20px", resize: "none"
              }}
              name="note"
              disabled={isNewProduct}
              value={details?.note}
              onChange={handleChange}
            />
          </Grid>

          {/* Upload file */}
          <Grid item xs={12}>
            <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Upload Photo</Typography>

            <Stack direction="row" flexWrap="wrap" gap={2} mb={2} alignItems="center">
              {/* List uploaded photos */}
              {details?.photos?.length > 0 && <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                {
                  details?.photos?.map((image, index) => (
                    <div style={{ position: "relative" }} key={index}>
                      <img
                        key={image}
                        src={image}
                        alt="Uploaded"
                        style={{
                          borderRadius: "20px",
                          width: "130px",
                          height: "130px",
                          objectFit: "cover",
                          boxShadow: "0 0 1px rgb(0 0 0 / 20%)",
                        }}
                      />
                      <IconButton
                        onClick={() => handleDeletePhoto(image)}
                        sx={{
                          position: "absolute",
                          top: "-10px",
                          right: "-10px",
                          borderRadius: "50%",
                          padding: "2px",
                          color: "#ff0000",
                          fill: "#ff0000",
                          backgroundColor: "#ffffff !important"
                        }}
                        size="small"
                      >
                        <DeleteIcon color="error" fontSize="small" width="16" height="16" />
                      </IconButton>
                    </div>
                  ))
                }
              </div>}

              <label htmlFor='product-image-input'>
                <Box
                  position="relative"
                  bgcolor="#F1F1F1"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexDirection="column"
                  minWidth="130px"
                  minHeight="130px"
                  borderRadius="20px"
                  p={2}
                  className="cur-pointer"
                >
                  {
                    isUploadingFile
                      ? <CircularProgress size={32} color='primary' sx={circularProgressIcon} />
                      : <Typography fontSize="small" fontWeight="semibold" textAlign="center">
                        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none">
                          <path fillRule="evenodd" clipRule="evenodd" d="M34.5827 14.7133C34.5827 13.4983 34.0993 12.3333 33.241 11.4733C31.636 9.87 28.4627 6.69667 26.8593 5.09167C25.9993 4.23333 24.8343 3.75 23.6193 3.75C21.4443 3.75 17.086 3.75 13.3327 3.75C11.2327 3.75 9.21935 4.58333 7.73435 6.06833C6.24935 7.55333 5.41602 9.56667 5.41602 11.6667V28.3333C5.41602 32.705 8.96102 36.25 13.3327 36.25H26.666C31.0377 36.25 34.5827 32.705 34.5827 28.3333C34.5827 23.5183 34.5827 17.4067 34.5827 14.7133ZM23.7493 6.25333C23.706 6.25167 23.6627 6.25 23.6193 6.25C21.4443 6.25 17.086 6.25 13.3327 6.25C11.896 6.25 10.5177 6.82 9.50268 7.83667C8.48602 8.85167 7.91602 10.23 7.91602 11.6667V28.3333C7.91602 31.325 10.341 33.75 13.3327 33.75H26.666C29.6577 33.75 32.0827 31.325 32.0827 28.3333C32.0827 23.5183 32.0827 17.4067 32.0827 14.7133C32.0827 14.67 32.081 14.6267 32.0793 14.5833H28.3327C27.1177 14.5833 25.951 14.1 25.091 13.2417C24.2327 12.3817 23.7493 11.215 23.7493 10V6.25333ZM30.3143 12.0833L26.2493 8.01833V10C26.2493 10.5533 26.4693 11.0817 26.8593 11.4733C27.251 11.8633 27.7793 12.0833 28.3327 12.0833H30.3143Z" fill="#0163FF" />
                          <path fillRule="evenodd" clipRule="evenodd" d="M20.0009 15.417H20.0309C20.1459 15.4203 20.2576 15.4387 20.3626 15.4703L20.3659 15.472C20.5559 15.5287 20.7342 15.6337 20.8842 15.7837L24.2176 19.117C24.7059 19.6037 24.7059 20.397 24.2176 20.8837C23.7309 21.372 22.9376 21.372 22.4509 20.8837L21.2509 19.6853V25.0003C21.2509 25.6903 20.6909 26.2503 20.0009 26.2503C19.3109 26.2503 18.7509 25.6903 18.7509 25.0003V19.6853L17.5509 20.8837C17.0642 21.372 16.2709 21.372 15.7842 20.8837C15.2959 20.397 15.2959 19.6037 15.7842 19.117L19.1176 15.7837C19.2676 15.6337 19.4459 15.5287 19.6359 15.472L19.6392 15.4703C19.7442 15.4387 19.8559 15.4203 19.9709 15.417H20.0009Z" fill="#0163FF" />
                        </svg>
                        <br />
                        Upload Photo
                      </Typography>
                  }
                </Box>
              </label>

              <input
                id="product-image-input"
                type="file"
                name="productImageFile"
                onChange={handleFileUpload}
                disabled={isUploadingFile || isNewProduct}
                style={{ display: 'none' }}
              />

            </Stack>
          </Grid>
        </Grid>
      </DialogContent >

      {/* Action buttons */}
      {!isNewProduct && <DialogActions DialogActions className='footer-modal' style={{ marginBottom: "12px", justifyContent: "start" }}>
        <MDButton
          sx={{ minWidth: '160px' }}
          color="primary"
          type='button'
          variant='contained'
          disabled={isLoading || isUploadingFile}
          onClick={handleSubmit}
        >
          {isLoading ? <CircularProgress size={22} color="light" /> : discrepancyId ? "Update" : "Save"}
        </MDButton>
        {
          discrepancyId &&
          <MDButton
            sx={{ minWidth: '160px' }}
            color="error"
            type='button'
            variant='contained'
            disabled={isDeleting || isUploadingFile}
            onClick={() => {
              if (!discrepancyId) return;
              dispatch(openCustomModalDialog({ modalData: { handleConfirm: handleDelete }, modal: "DELETE_DISCREPANCY" }))
            }}
          // onClick={handleDelete}
          >
            {isDeleting ? <CircularProgress size={22} color="light" /> : "Delete"}
          </MDButton>
        }
        <MDButton
          onClick={closeModal}
          sx={{ minWidth: '160px' }}
          type='button'
          variant='outlined'
          color='dark'
        >
          Cancel
        </MDButton>
      </DialogActions>}
    </>
  );
}